/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.heading = this.querySelector('#quick-add-heading');
      this.defaultHeadingText = this.heading ? this.heading.textContent.trim() : '';
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Normalizes a product URL to its canonical /products/{handle} form.
     * @param {string} url - The product URL (may be collection-scoped).
     * @returns {string}
     */
    normalizeProductUrl(url) {
      return url.replace(/\/collections\/[^/]+/, '');
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      const el = evt.target.closest('.js-quick-add') || evt.target;
      const url = this.normalizeProductUrl(el.dataset.productUrl);
      if (!this.fetchedUrls.includes(url)) {
        this.fetch = {
          url: url,
          promise: fetch(url)
        };
        this.fetchedUrls.push(url);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      const quickAddEl = evt.target.closest('.js-quick-add');
      if (!quickAddEl) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(quickAddEl);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(quickAddEl);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      const normalizedUrl = this.normalizeProductUrl(opener.dataset.productUrl);

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === normalizedUrl) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = normalizedUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');

      // Reset heading to default while loading
      if (this.heading) {
        this.heading.textContent = this.defaultHeadingText;
      }
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== normalizedUrl) {
        this.fetch = {
          url: normalizedUrl,
          promise: fetch(normalizedUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.js-product');
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Extract size chart (custom-liquid block with details-disclosure containing "Size Chart")
      this.sizeChartHtml = '';
      const allDisclosures = this.productEl.querySelectorAll('details-disclosure');
      allDisclosures.forEach((dd) => {
        const summary = dd.querySelector('summary');
        if (summary && summary.textContent.includes('Size Chart')) {
          const details = dd.querySelector('details');
          if (details) details.setAttribute('open', '');
          this.sizeChartHtml = dd.outerHTML;
        }
      });
      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      // Extract all product media images for gallery
      this.galleryImages = [];
      const mediaItems = this.productEl.querySelectorAll('.media-viewer__item[data-media-type="image"]');
      mediaItems.forEach((item) => {
        const img = item.querySelector('img:not(.zoom-image)');
        if (img) {
          // Try src first (eagerly loaded), then data-src (lazy loaded), then srcset/data-srcset
          let rawSrc = img.getAttribute('src') || img.getAttribute('data-src') || '';
          if (!rawSrc || rawSrc.startsWith('data:')) {
            // Fallback: extract from srcset or data-srcset
            const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset') || '';
            const firstEntry = srcset.split(',')[0];
            if (firstEntry) rawSrc = firstEntry.trim().split(' ')[0];
          }
          const src = rawSrc.split('&width=')[0];
          if (src && !src.startsWith('data:')) {
            this.galleryImages.push({
              src: src,
              alt: img.alt || '',
              mediaId: item.dataset.mediaId
            });
          }
        }
      });


      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__header md:hidden">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="quick-add-info__title-price">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
              <span class="product-price product-info__price">
                ${this.getElementHtml('.product-info__price')}
              </span>
            </div>
          </div>
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}${this.galleryImages && this.galleryImages.length > 0 ? ' quick-add-info__media--clickable' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="quick-add-description rte text-sm">
              ${this.getElementHtml('.product-description')}
            </div>
            <div class="quick-add-view-details">
              <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
            </div>
          </div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options')}
        </div>
        ${this.sizeChartHtml ? `<div class="quick-add-size-chart">${this.sizeChartHtml}</div>` : ''}
        ${(() => {
          const upsellBundleEl = this.productEl.querySelector('.quick-add-bundle-variants');
          let upsellProducts = [];
          try {
            if (upsellBundleEl) {
              upsellProducts = JSON.parse(upsellBundleEl.dataset.products || '[]');
            }
          } catch (e) {
            // Invalid upsell JSON
            upsellProducts = [];
          }

          if (!Array.isArray(upsellProducts) || upsellProducts.length === 0) return '';

          const cards = upsellProducts.map((product, index) => `
            <div class="product-additional-card" data-id="${product.variant_id}">
              <input type="checkbox" class="upsell-checkbox" data-index="${index}" id="upsell-${product.variant_id}">
              <label for="upsell-${product.variant_id}">
                <div class="card card--row card--related relative flex">
                  <div class="card__media">
                    <div class="media relative" style="padding-top: 151.47%;">
                      <img
                        src="${product.image}"
                        class="img-fit no-js-hidden"
                        loading="lazy"
                        width="176"
                        height="267"
                        alt=""
                        srcset="${product.image} 1x, ${product.image.replace('&width=176', '&width=352')} 2x"
                      >
                    </div>
                  </div>

                  <div class="card__info">
                    <p class="h6 regular-text mt-2 mb-1">
                      <p class="card-link text-current">
                        ${product.title}
                      </p>
                    </p>

                    <div class="price">
                      <div class="price__default">
                        <strong class="price__current">${product.price}</strong>
                        <s class="price__was"></s>
                      </div>
                    </div>

                  </div>
                </div>
               </label>
            </div>
          `).join('');

          const cartDrawerEl = document.querySelector('cart-drawer');
          const headingTemplate = (cartDrawerEl?.dataset?.upsellLabel || 'Add one pack of wipes for [price]');
          const headingHtml = headingTemplate.replace('[price]', '<span></span>');
          return `
            <div class="product-additional-upsell">
              <p class="product-additional-upsell-title">${headingHtml}</p>
              <div class="product-additional-cards">${cards}</div>
            </div>
          `;
        })()}
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      // Insert price range into .product-additional-upsell-title span
      const bundleEl = this.productEl.querySelector('.quick-add-bundle-variants');
      const priceSpan = this.querySelector('.product-additional-upsell-title span');

      if (bundleEl && priceSpan) {
        try {
          const products = JSON.parse(bundleEl.dataset.products || '[]');

          const prices = products.map(p => parseFloat(p.price.replace(/[^0-9.]/g, ''))).filter(p => !isNaN(p));

          if (prices.length) {
            const min = Math.min(...prices);
            const max = Math.max(...prices);

            if (min === max) {
              priceSpan.textContent = `$${min.toFixed(2)}`;
            } else {
              priceSpan.textContent = `$${min.toFixed(2)} – $${max.toFixed(2)}`;
            }
          }
        } catch (e) {
          // Failed to parse product prices
        }
      }

      // Update drawer heading with product title + price (desktop only)
      if (this.heading) {
        const title = this.getElementHtml('.product-title');
        const price = this.getElementHtml('.product-info__price');
        this.heading.innerHTML = `
          <span class="quick-add-heading__title">${title}</span>
          <span class="quick-add-heading__price product-info__price">${price}</span>
        `;
      }

      // Set up image gallery click handler
      const mediaContainer = this.querySelector('.quick-add-info__media');
      if (mediaContainer && this.galleryImages && this.galleryImages.length > 0) {
        mediaContainer.addEventListener('click', () => this.openGalleryModal());
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');

      const form = this.querySelector('#product-form-quickadd');

      this.querySelectorAll('.upsell-checkbox').forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
          const card = checkbox.closest('.product-additional-card');
          const variantId = card?.dataset.id;
          const index = checkbox.dataset.index;

          if (!form || !variantId || index === undefined) return;

          // Remove existing upsell inputs for this variant
          form.querySelectorAll(`[data-upsell="${variantId}"]`).forEach(el => el.remove());

          // If checkbox is checked, add the inputs
          if (checkbox.checked) {
            // Find the next available line item index
            const existingItems = form.querySelectorAll('input[name*="[id]"]');
            const nextIndex = existingItems.length;

            const idInput = document.createElement('input');
            idInput.type = 'hidden';
            idInput.name = `items[${nextIndex}][id]`;
            idInput.value = variantId;
            idInput.setAttribute('data-upsell', variantId);

            const qtyInput = document.createElement('input');
            qtyInput.type = 'hidden';
            qtyInput.name = `items[${nextIndex}][quantity]`;
            qtyInput.value = '1';
            qtyInput.setAttribute('data-upsell', variantId);

            form.appendChild(idInput);
            form.appendChild(qtyInput);
          }
        });
      });
    }

    /**
     * Gets the innerHTML of an element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Opens a modal with all product images in a navigable gallery.
     */
    openGalleryModal() {
      if (!this.galleryImages || !this.galleryImages.length) return;

      // Remove any existing gallery modal
      const existingModal = document.getElementById('quick-add-gallery-modal');
      if (existingModal) existingModal.remove();

      // Create modal on document.body (outside the drawer to avoid stacking/event issues)
      const modal = document.createElement('modal-dialog');
      modal.className = 'modal fixed top-0 left-0 w-full h-full flex items-center justify-center';
      modal.id = 'quick-add-gallery-modal';
      modal.innerHTML = `
        <div class="modal__window quick-add-gallery__window relative bg-theme-bg text-theme-text overflow-hidden has-motion" role="dialog" aria-label="Product gallery" aria-modal="true" tabindex="-1">
          <button type="button" class="modal__close-btn absolute js-close-modal">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false" role="presentation"><path d="M1 17L17 1M1 1l16 16" stroke="currentColor" stroke-width="2"/></svg>
            <span class="visually-hidden">Close</span>
          </button>
          <div class="quick-add-gallery__content"></div>
        </div>
      `;
      document.body.appendChild(modal);

      // Override open/close to skip scroll-lock — the quick-add drawer already manages it.
      // Without this, Modal.close() removes the 'fixed' class and resets body.style.top,
      // breaking the drawer's scroll lock while it's still open.
      modal.open = function(opener) {
        this.setAttribute('open', '');
        this.openedBy = opener;
        trapFocus(this);
        this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();
        this.addEventListener('keyup', this.keyupHandler);
      };
      modal.close = function() {
        this.removeEventListener('keyup', this.keyupHandler);
        this.removeAttribute('open');
        removeTrapFocus(this.openedBy);
        this.remove();
      };

      // Add direct click handler on close button (modal's handleClick uses evt.target.matches
      // which fails when clicking SVG/path children inside the button)
      const closeBtn = modal.querySelector('.js-close-modal');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => modal.close());
      }

      const content = modal.querySelector('.quick-add-gallery__content');
      content.innerHTML = this.galleryImages.map((img, index) => `
        <div class="quick-add-gallery__item${index === 0 ? ' is-active' : ''}">
          <img src="${img.src}&width=800"
               srcset="${img.src}&width=600 600w, ${img.src}&width=800 800w, ${img.src}&width=1200 1200w"
               sizes="(min-width: 800px) 760px, 90vw"
               alt="${img.alt}"
               loading="${index === 0 ? 'eager' : 'lazy'}">
        </div>
      `).join('');

      if (this.galleryImages.length > 1) {
        content.insertAdjacentHTML('beforeend', `
          <button type="button" class="quick-add-gallery__nav quick-add-gallery__nav--prev" aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
          <button type="button" class="quick-add-gallery__nav quick-add-gallery__nav--next" aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
          </button>
          <div class="quick-add-gallery__counter">
            <span class="quick-add-gallery__current">1</span> / ${this.galleryImages.length}
          </div>
        `);

        let currentIndex = 0;
        const items = content.querySelectorAll('.quick-add-gallery__item');
        const counter = content.querySelector('.quick-add-gallery__current');

        const showImage = (index) => {
          items.forEach(item => item.classList.remove('is-active'));
          items[index].classList.add('is-active');
          counter.textContent = index + 1;
          currentIndex = index;
        };

        content.querySelector('.quick-add-gallery__nav--prev').addEventListener('click', () => {
          showImage(currentIndex === 0 ? items.length - 1 : currentIndex - 1);
        });
        content.querySelector('.quick-add-gallery__nav--next').addEventListener('click', () => {
          showImage(currentIndex === items.length - 1 ? 0 : currentIndex + 1);
        });
      }

      modal.open(this.querySelector('.quick-add-info__media'));
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        // If an upsell is selected, switch the alert heading to plural
        const hasUpsell = this.querySelectorAll('.upsell-checkbox:checked').length > 0;
        const headingEl = this.notification.querySelector('.quick-add__alert-heading');
        if (headingEl) {
          const singular = headingEl.dataset.singular || headingEl.textContent.trim();
          headingEl.dataset.singular = singular;
          const plural = singular.replace(/\bItem\b/i, 'Items');
          headingEl.textContent = hasUpsell ? plural : singular;
        }

        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
